import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Grid,
    Modal,
    Tooltip,
    useTheme,
    alpha,
    Alert,
    Divider,
    Container
} from '@mui/material';
import { Add, HelpOutline, Delete } from '@mui/icons-material';
import SingleFileUpload from "../../../fileUpload/FileUpload";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateMediaDocuments, createMedia, fetchMedia } from '../../../../store/startup/media/mediaSlice';
import { toast } from 'react-toastify';

const Media = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const [open, setOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [videos, setVideos] = useState([]);
    const [errors, setErrors] = useState({});
    const [showcasePhotoPath, setShowcasePhotoPath] = useState('');

    useEffect(() => {
        dispatch(fetchMedia(startupId));
    }, [dispatch, startupId]);

    const mediaList = useSelector((state) => state.media.mediaList || []); // Ensure mediaList is an array
    console.log("mediaList", mediaList)
    const handleVideoUrlChange = (event) => {
        setVideoUrl(event.target.value);
        if (errors.videoUrl) setErrors(prev => ({ ...prev, videoUrl: '' }));
    };

    const handleAddVideo = () => {
        if (!videoUrl.trim()) {
            setErrors(prev => ({ ...prev, videoUrl: 'Video URL boş bırakılamaz' }));
            return;
        }
        setVideos(prev => [...prev, videoUrl]);
        setVideoUrl('');
    };

    const handleRemoveVideo = (index) => {
        setVideos(prev => prev.filter((_, i) => i !== index));
    };

    const handleMediaCreation = async (mediaType, mediaPath) => {
        try {
            await dispatch(createMedia({ startupId, mediaType, mediaPath })).unwrap();
            dispatch(fetchMedia(startupId));
        } catch (error) {
            console.error('Failed to create media:', error);
        }
    };

    const handleShowcasePhotoUpload = async (filePath) => {
        try {
            await handleMediaCreation(1, filePath);
            setShowcasePhotoPath(filePath);
            toast.success('Vitrin fotoğrafı güncellendi');
        } catch (error) {
            console.error('Failed to update showcase photo:', error);
            toast.error('Vitrin fotoğrafı güncellenirken hata oluştu');
        }
    };

    const setShowcasePhoto = () => {
        mediaList.forEach((media) => {
            if (media.mediaType === 1) {
                setShowcasePhotoPath(media.mediaPath);
            }
        })
    }

    useEffect(() => {
        setShowcasePhoto();
    }, [mediaList]);

    return (
        <Box>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Fotoğraf ve Çizimler
                </Typography>
                <Tooltip title="Görsel boyutları en az 800x600px olmalıdır">
                    <IconButton
                        size="small"
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        <HelpOutline />
                    </IconButton>
                </Tooltip>
            </Box>
            <Divider sx={{ marginBottom: 3, bgcolor: 'black' }} />
            <Box>
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        borderRadius: 2
                    }}
                >
                    Projenizi en iyi şekilde tanıtacak görseller ekleyin. Vitrin fotoğrafı kampanya listeleme sayfasında görünecektir.
                </Alert>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                            Vitrin Fotoğrafı *
                        </Typography>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200,
                            border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderRadius: 2,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                            }
                        }}>
                            <SingleFileUpload 
                                onUploadComplete={handleShowcasePhotoUpload}
                                existFilePath={showcasePhotoPath}
                                fileUploadPath="startup"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                            Diğer Fotoğraflar
                        </Typography>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200,
                            border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderRadius: 2,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                            }
                        }}>
                            <SingleFileUpload 
                                onUploadComplete={(filePath) => handleMediaCreation(2, filePath)}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box>
                    {/* Display fetched media */}
                    {mediaList.length > 0 ? (
                        mediaList.map((media) => (
                            <Box key={media.startupMediaId} sx={{ mt: 2 }}>
                                {media.mediaType === 2 && (
                                <SingleFileUpload existFilePath={media.mediaPath}></SingleFileUpload>
                                )}
                            </Box>
                        ))
                    ) : (
                        <Typography>Medya bulunamadı</Typography>
                    )}
                </Box>
            </Box>


            <Box sx={{
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Videolar
                </Typography>
                <Tooltip title="Youtube veya Vimeo video linkleri ekleyebilirsiniz">
                    <IconButton
                        size="small"
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        <HelpOutline />
                    </IconButton>
                </Tooltip>
            </Box>
            <Divider sx={{ marginBottom: 3, bgcolor: 'black' }} />

            <Box>
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        borderRadius: 2
                    }}
                >
                    Projenizi anlatan videoların Youtube veya Vimeo linkini giriniz.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Video URL"
                        value={videoUrl}
                        onChange={handleVideoUrlChange}
                        error={!!errors.videoUrl}
                        helperText={errors.videoUrl}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddVideo}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Ekle
                    </Button>
                </Box>

                {videos.map((url, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 1
                        }}
                    >
                        <Typography>{url}</Typography>
                        <IconButton
                            onClick={() => handleRemoveVideo(index)}
                            sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                },
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default Media;